import React from "react";
import { UNDERDOG_STYLES, UNDERDOG_LEGEND, UNDERDOG_LEGEND_ELO, UNDERDOG_CAP } from "@/lib/underdog";

/** Compact badge shown on a match — classification + bonus points. */
export function UnderdogBadge({ info, underdogName, className = "" }) {
  if (!info) return null;
  const s = UNDERDOG_STYLES[info.level];
  const detail = info.system === "elo" ? `${info.underdogPct}% win prob` : `rank gap ${info.rankGap}`;
  const title = info.finalBonus > 0
    ? `${underdogName || "Underdog"} to win → +${info.finalBonus} bonus (${detail})`
    : `Evenly matched (${detail}) — no underdog bonus`;
  return (
    <span
      data-testid="underdog-badge"
      title={title}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 border ${s.border} ${s.chip} text-[10px] uppercase tracking-widest font-display ${s.text} w-fit ${className}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {info.label}
      {info.system === "elo" && <span className="text-zinc-400 normal-case">{info.underdogPct}%</span>}
      {info.finalBonus > 0 && <span className="text-white">+{info.finalBonus}</span>}
    </span>
  );
}

/** Tiny marker placed next to the underdog team. */
export function UnderdogTag({ info }) {
  if (!info) return null;
  const s = UNDERDOG_STYLES[info.level];
  return (
    <span
      data-testid="underdog-team-tag"
      title={`Underdog — +${info.finalBonus} if they win`}
      className={`inline-flex items-center gap-1 px-1 py-0.5 border ${s.border} ${s.chip} ${s.text} text-[8px] uppercase tracking-wider font-display`}
    >
      <span className={`w-1 h-1 rounded-full ${s.dot}`} />
      <span className="hidden sm:inline">Underdog</span>
    </span>
  );
}

/** Legend explaining the badge levels + cap. `elo` switches to the league (Elo) guide. */
export function UnderdogLegend({ className = "", elo = false }) {
  const tiers = elo ? UNDERDOG_LEGEND_ELO : UNDERDOG_LEGEND;
  return (
    <div className={`surface-card p-4 ${className}`} data-testid="underdog-legend">
      <div className="label-tag mb-2">Underdog Bonus Guide</div>
      <p className="text-xs text-zinc-400 mb-3">
        {elo ? (
          <>Each match is classified by the <span className="text-zinc-200">underdog's Elo win probability</span> (home advantage applied).</>
        ) : (
          <>Each match is classified by the <span className="text-zinc-200">FIFA rank gap</span> between the two teams.</>
        )}{" "}
        Correctly predicting the <span className="text-zinc-200">underdog</span> to win earns the full bonus on top
        of your normal points. Correctly predicting a <span className="text-zinc-200">draw</span> earns <span className="text-zinc-200">half</span> the bonus (rounded up). A wrong prediction earns nothing.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {tiers.map((l) => {
          const s = UNDERDOG_STYLES[l.level];
          return (
            <div key={l.level} className={`flex items-center gap-2 border ${s.border} ${s.chip} px-2.5 py-1.5`} data-testid={`underdog-legend-${l.level}`}>
              <span className={`w-2 h-2 rounded-full ${s.dot} flex-shrink-0`} />
              <span className={`text-xs font-display uppercase tracking-wider ${s.text}`}>{l.label}</span>
              <span className="text-[11px] text-zinc-500 ml-auto whitespace-nowrap">{l.range} · +{l.base}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
        {elo ? (
          <>Bonus comes straight from the Elo probability table — capped at <span className="text-primary-accent">+{UNDERDOG_CAP}</span>.</>
        ) : (
          <><span className="text-zinc-300">Favourite tier bonus</span> (always added): favourite ranked Top&nbsp;10 → +2, Top&nbsp;25 → +1.
          Final bonus = base + tier bonus, capped at <span className="text-primary-accent">+{UNDERDOG_CAP}</span>.</>
        )}
      </div>
    </div>
  );
}
